"use client";

import { useState } from "react";
import { EmojiPickerWrapper } from "./EmojiPickerWrapper";
import { GifPicker } from "./GifPicker";

export type MediaTab = "emoji" | "gifs" | "stickers" | "memes" | "clips";

export interface MediaPickerProps {
  /** Which tab opens first — set by whichever composer button was pressed. */
  defaultTab?: MediaTab;
  onEmoji: (emoji: string) => void;
  onGif: (url: string) => void;
  onSticker: (url: string) => void;
  onMeme: (url: string) => void;
  onClip: (url: string) => void;
  /** Posts an AI-generated sticker. Omit to hide the generator. */
  onGenerateSticker?: (file: File) => Promise<void> | void;
}

const TABS: Array<{ id: MediaTab; label: string }> = [
  { id: "emoji", label: "Emoji" },
  { id: "gifs", label: "GIFs" },
  { id: "stickers", label: "Stickers" },
  { id: "memes", label: "Memes" },
  { id: "clips", label: "Clips" },
];

/**
 * One panel, three libraries — the way Discord does it. Each composer button
 * opens this same panel on its own tab, so switching between emoji, GIFs and
 * stickers never means closing a popover and finding another button.
 */
export function MediaPicker({ defaultTab = "emoji", onEmoji, onGif, onSticker, onMeme, onClip, onGenerateSticker }: MediaPickerProps) {
  const [tab, setTab] = useState<MediaTab>(defaultTab);

  return (
    <div className="w-96">
      <div className="flex items-center gap-1 p-1.5 border-b border-zinc-200 dark:border-zinc-700">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            aria-pressed={tab === t.id}
            className={`flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors ${
              tab === t.id
                ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100"
                : "text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Each tab unmounts when hidden: emoji-mart is a custom element with its
          own shadow DOM and the GIF grid holds in-flight requests, so keeping
          all three alive would mean three pickers fighting over focus. */}
      {tab === "emoji" && <EmojiPickerWrapper onSelect={onEmoji} />}
      {tab === "gifs" && <GifPicker kind="gifs" onSelect={onGif} />}
      {tab === "stickers" && (
        <GifPicker kind="stickers" onSelect={onSticker} onGenerate={onGenerateSticker} />
      )}
      {tab === "memes" && <GifPicker kind="memes" onSelect={onMeme} />}
      {tab === "clips" && <GifPicker kind="clips" onSelect={onClip} />}
    </div>
  );
}
